import { useReactiveVar } from "@apollo/client";
import { useState } from "react";
import { itemVariantVar } from "~/components/stores/detail-product/store";

import { openTextOnlyToast } from "~/components/stores/toast/store";
import { useQueryString } from "~/hooks/use-query-string";
import type { G_GetVideoQuery, G_FetchViewerVideoBookmarksQuery } from "~/lib/graphql";
import { useAddVideoBookmarkMutation, useDeleteVideoBookmarksMutation, GetVideoDocument } from "~/lib/graphql";
import { TRACKING_PRICE } from "~/lib/tracking/const";
import { sendTrackingAddToWishlist } from "~/lib/tracking/events";
import { getDevice, getPlayDevice } from "~/util/get-device";

type UseMobileActionButtonArgs = {
  videoData: G_GetVideoQuery["video"];
};

export const useMobileActionButton = ({ videoData }: UseMobileActionButtonArgs) => {
  const [addVideoBookmarkExec, { loading: isLoadingAddVideoBookmark }] = useAddVideoBookmarkMutation();
  const [deleteVideoBookmarksExec, { loading: isLoadingDeleteVideoBookmarks }] = useDeleteVideoBookmarksMutation();

  const itemVariant = useReactiveVar(itemVariantVar);

  const { season, content } = useQueryString();
  const [isFavoriteRegistered, setIsFavoriteRegistered] = useState(videoData.hasBookmark);

  const deleteBookmarkHandler = async () => {
    if (isLoadingDeleteVideoBookmarks) return;

    const { data } = await deleteVideoBookmarksExec({
      variables: {
        input: {
          seasonIds: [season],
        },
      },
      update: (cache, { data }) => {
        if (data?.deleteVideoBookmarks.mutationId) {
          // memo: お気に入り一覧のcacheを更新
          cache.modify({
            fields: {
              viewer(existing: G_FetchViewerVideoBookmarksQuery["viewer"]) {
                // eslint-disable-next-line unused-imports/no-unused-vars
                const { videoBookmarks, ...rest } = existing;
                return {
                  ...rest,
                } as G_FetchViewerVideoBookmarksQuery["viewer"];
              },
            },
          });

          // memo: 作品末端自身のcacheを更新
          cache.writeQuery({
            query: GetVideoDocument,
            variables: {
              seasonId: season,
              contentId: content ?? "",
              device: getDevice(),
              playDevice: getPlayDevice(),
              isLoggedIn: true,
              isContentId: content !== undefined,
            },
            data: {
              video: {
                ...videoData,
                hasBookmark: false,
              },
            },
          });

          // memo: 到達不可能なキャッシュを削除
          cache.gc();
        }
      },
    });

    if (!data) return;

    if (data.deleteVideoBookmarks.mutationId) {
      setIsFavoriteRegistered(false);
      openTextOnlyToast("DELETE_BOOKMARK");
    } else {
      openTextOnlyToast("FAILED_DELETE_BOOKMARK");
    }
  };

  const addBookmarkHandler = async () => {
    if (isLoadingAddVideoBookmark) return;

    const { data } = await addVideoBookmarkExec({
      variables: {
        input: {
          seasonId: season,
        },
      },
      update: (cache, { data }) => {
        if (data?.addVideoBookmark.mutationId) {
          // memo:お気に入り一覧のcacheを更新
          cache.modify({
            fields: {
              viewer(existing: G_FetchViewerVideoBookmarksQuery["viewer"]) {
                // eslint-disable-next-line unused-imports/no-unused-vars
                const { videoBookmarks, ...rest } = existing;
                return {
                  ...rest,
                } as G_FetchViewerVideoBookmarksQuery["viewer"];
              },
            },
          });
          // memo:作品末端自身のcacheを更新
          cache.writeQuery({
            query: GetVideoDocument,
            variables: {
              seasonId: season,
              contentId: content ?? "",
              device: getDevice(),
              playDevice: getPlayDevice(),
              isLoggedIn: true,
              isContentId: content !== undefined,
            },
            data: {
              video: {
                ...videoData,
                hasBookmark: true,
              },
            },
          });
          // memo: 到達不可能なキャッシュを削除
          cache.gc();
        }
      },
    });

    if (!data) return;

    if (data.addVideoBookmark.mutationId) {
      setIsFavoriteRegistered(true);
      openTextOnlyToast("ADD_BOOKMARK");

      sendTrackingAddToWishlist({
        seasonId: season,
        item_name: videoData.titleName,
        priceInfo: { price: TRACKING_PRICE },
        itemVariant,
      });
    } else {
      openTextOnlyToast("FAILED_ADD_BOOKMARK");
    }
  };

  return {
    isFavoriteRegistered,
    addBookmarkHandler,
    deleteBookmarkHandler,
  };
};
